import React, { useState, useEffect, useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import api from '../services/api';
import { useCache } from '../hooks/useCache';

const AccountGrowthChart = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('سنة');

  const months = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];

  // Determine number of months based on period
  const monthsCount = useMemo(() => {
    if (period === 'شهر') return 1;
    if (period === 'أسبوع') return 1;
    return 12; // سنة
  }, [period]);

  const fetcher = useMemo(() => () => api.getGrowthReport(monthsCount), [monthsCount]);
  const { data: cachedData, loading: cacheLoading, refetch } = useCache(
    `growth_report_${monthsCount}`,
    fetcher,
    { ttl: 5 * 60 * 1000 } // 5 minutes cache
  );

  useEffect(() => {
    fetchGrowthData();
  }, [period, cachedData]);

  const fetchGrowthData = async () => {
    try {
      setLoading(true);
      
      // Use cached data if available
      const response = cachedData || await api.getGrowthReport(monthsCount);
      
      if (response && response.data && Array.isArray(response.data)) {
        // Transform data for chart - show total growth
        const transformed = response.data.map(item => ({
          month: item.month,
          value: item.users + item.restaurants + item.stores, // Total accounts
          users: item.users,
          restaurants: item.restaurants,
          stores: item.stores
        }));
        setData(transformed);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching growth data:', error);
      if (error.message && error.message.includes('لا يمكن الاتصال')) {
        console.warn('⚠️ الباك اند غير متاح. تأكد من تشغيله على http://localhost:3000');
      }
      // Fallback to empty data
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const maxValue = data.length > 0 ? Math.max(...data.map(d => d.value || 0)) : 1000;

  return (
    <div style={{ 
      width: '100%',
      height: '100%',
      minHeight: '600px',
      backgroundColor: '#FFFFFF',
      borderRadius: '16px',
      padding: '32px',
      border: '1px solid rgba(233, 106, 58, 0.15)',
      boxShadow: '0px 4px 23px rgba(0, 0, 0, 0.04)',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
      direction: 'rtl'
    }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'row',
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '32px',
        width: '100%',
        paddingBottom: '20px',
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
      }}>
        <div style={{ 
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div style={{ 
            width: '4px',
            height: '24px',
            backgroundColor: '#3FB984',
            borderRadius: '2px'
          }}></div>
          <h2 style={{ 
            color: '#2B2F3F',
            fontSize: '20px',
            fontFamily: 'El Messiri, sans-serif',
            fontWeight: 700,
            textAlign: 'right',
            margin: 0
          }}>
            نمو الحسابات
          </h2>
        </div>

        <div style={{ 
          display: 'flex',
          flexDirection: 'row',
          gap: '8px',
          alignItems: 'center'
        }}>
          {['سنة', 'شهر', 'أسبوع'].map((periodOption, index) => (
            <button
              key={index}
              onClick={() => setPeriod(periodOption)}
              style={{
                padding: '6px 14px',
                borderRadius: '8px',
                border: period === periodOption ? 'none' : '1px solid rgba(102, 106, 122, 0.22)',
                backgroundColor: period === periodOption ? '#3FB984' : 'rgba(176, 179, 187, 0.13)',
                color: period === periodOption ? '#FFFFFF' : '#666A7A',
                fontFamily: 'Cairo, sans-serif',
                fontSize: '12px',
                fontWeight: period === periodOption ? 600 : 500,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (period !== periodOption) {
                  e.currentTarget.style.backgroundColor = 'rgba(176, 179, 187, 0.2)';
                }
              }}
              onMouseLeave={(e) => {
                if (period !== periodOption) {
                  e.currentTarget.style.backgroundColor = 'rgba(176, 179, 187, 0.13)';
                }
              }}
            >
              {periodOption}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div style={{ 
        width: '100%',
        flex: 1,
        minHeight: '450px'
      }}>
        {loading ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            color: '#666A7A',
            fontFamily: 'Cairo, sans-serif'
          }}>
            جاري التحميل...
          </div>
        ) : data.length === 0 ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            color: '#666A7A',
            fontFamily: 'Cairo, sans-serif'
          }}>
            لا توجد بيانات
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={data}
              margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
            >
            <defs>
              <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3FB984" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#3FB984" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke="#E5E7EB" 
              strokeWidth={1}
              vertical={false}
            />
            <XAxis
              dataKey="month"
              stroke="#9CA3AF"
              tick={{ fill: '#6B7280', fontSize: '13px', fontFamily: 'Cairo, sans-serif' }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              stroke="#9CA3AF"
              tick={{ fill: '#6B7280', fontSize: '13px', fontFamily: 'Cairo, sans-serif' }}
              domain={[0, maxValue * 1.2]}
              tickFormatter={(value) => {
                if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
                return value.toString();
              }}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#FFFFFF',
                border: '1px solid #E5E7EB',
                borderRadius: '12px',
                fontFamily: 'Cairo, sans-serif',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                padding: '12px 16px'
              }}
              labelStyle={{
                color: '#2B2F3F',
                fontFamily: 'Cairo, sans-serif',
                fontSize: '13px',
                fontWeight: 600,
                marginBottom: '8px'
              }}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke="#3FB984"
              strokeWidth={2.5}
              fillOpacity={1}
              fill="url(#colorValue)"
            />
          </AreaChart>
        </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default AccountGrowthChart;
